import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Loader2, CheckCircle2, CreditCard, XCircle, ExternalLink, Ticket } from "lucide-react";
import { api } from "../api.js";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card.js";
import { Button } from "../components/ui/button.js";
import { Badge } from "../components/ui/badge.js";
import { Input } from "../components/ui/input.js";
import { Label } from "../components/ui/label.js";

interface Plan {
  id: number;
  nombre: string;
  descripcion: string | null;
  precio: number;
  periodo: string;
}

interface Suscripcion {
  id: number;
  estado: string;
  fechaInicio: string | null;
  fechaFin: string | null;
  autoRenueva: boolean;
  mpPreapprovalId: string | null;
  plan: { id: number; nombre: string; precio: number; periodo: string };
  pagos: {
    id: number;
    mpPaymentId: string | null;
    monto: number;
    estado: string;
    fechaPago: string | null;
  }[];
}

export default function SuscripcionPage() {
  const [planes, setPlanes] = useState<Plan[]>([]);
  const [suscripcion, setSuscripcion] = useState<Suscripcion | null>(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState<number | null>(null);
  const [initPoint, setInitPoint] = useState<string | null>(null);
  const [cuponCodigo, setCuponCodigo] = useState("");
  const [cuponValido, setCuponValido] = useState<null | { valido: boolean; tipo?: string; valor?: number; error?: string }>(null);
  const [validandoCupon, setValidandoCupon] = useState(false);
  const [aplicandoCupon, setAplicandoCupon] = useState(false);

  useEffect(() => {
    Promise.all([
      api<{ planes: Plan[] }>("/planes"),
      api<{ suscripcion: Suscripcion | null; estado?: string }>("/suscripcion"),
    ])
      .then(([p, s]) => {
        setPlanes(p.planes);
        setSuscripcion(s.suscripcion);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleCrear = async (planId: number) => {
    setCreating(planId);
    try {
      const res = await api<{ suscripcionId: number; initPoint: string }>("/suscripcion/crear", {
        method: "POST",
        body: JSON.stringify({ planId }),
      });
      setInitPoint(res.initPoint);
      toast.success("Suscripción creada. Redirigiendo a Mercado Pago...");
      window.open(res.initPoint, "_blank");
      // Refrescar estado
      const s = await api<{ suscripcion: Suscripcion | null }>("/suscripcion");
      setSuscripcion(s.suscripcion);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Error al crear suscripción";
      toast.error(msg);
    } finally {
      setCreating(null);
    }
  };

  const handleValidarCupon = async () => {
    if (!cuponCodigo.trim()) return;
    setValidandoCupon(true);
    try {
      const res = await api<{ valido: boolean; tipo?: string; valor?: number; error?: string }>("/cupones/validar", {
        method: "POST",
        body: JSON.stringify({ codigo: cuponCodigo.trim() }),
      });
      setCuponValido(res);
      if (res.valido) toast.success(`Cupón válido: ${res.tipo} (${res.valor})`);
      else toast.error(res.error || "Cupón inválido");
    } catch {
      toast.error("Error al validar cupón");
    } finally {
      setValidandoCupon(false);
    }
  };

  const handleAplicarCupon = async () => {
    if (!suscripcion || !cuponCodigo.trim()) return;
    setAplicandoCupon(true);
    try {
      const res = await api<{ ok: boolean; tipo: string; valor: number; nuevoEstado: string }>("/cupones/aplicar", {
        method: "POST",
        body: JSON.stringify({ codigo: cuponCodigo.trim(), suscripcionId: suscripcion.id }),
      });
      toast.success(`Cupón aplicado: ${res.tipo}`);
      setCuponCodigo("");
      setCuponValido(null);
      const s = await api<{ suscripcion: Suscripcion | null }>("/suscripcion");
      setSuscripcion(s.suscripcion);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Error al aplicar cupón";
      toast.error(msg);
    } finally {
      setAplicandoCupon(false);
    }
  };

  const handleSync = async () => {
    try {
      await api("/suscripcion/sync", { method: "POST" });
      const s = await api<{ suscripcion: Suscripcion | null }>("/suscripcion");
      setSuscripcion(s.suscripcion);
      toast.success("Estado sincronizado");
    } catch {
      toast.error("Error al sincronizar");
    }
  };

  const handleCancelar = async () => {
    if (!window.confirm("¿Cancelar tu suscripción?")) return;
    try {
      await api("/suscripcion/cancelar", { method: "POST" });
      const s = await api<{ suscripcion: Suscripcion | null }>("/suscripcion");
      setSuscripcion(s.suscripcion);
      toast.success("Suscripción cancelada");
    } catch {
      toast.error("Error al cancelar");
    }
  };

  const estadoBadge = (estado: string) => {
    const map: Record<string, { variant: "default" | "secondary" | "destructive" | "outline"; label: string }> = {
      ACTIVA: { variant: "default", label: "Activa" },
      PENDIENTE_PAGO: { variant: "secondary", label: "Pendiente de pago" },
      GRACIA: { variant: "outline", label: "Período de gracia" },
      CANCELADA: { variant: "destructive", label: "Cancelada" },
      EXPIRADA: { variant: "destructive", label: "Expirada" },
      GRATIS: { variant: "default", label: "Gratis" },
    };
    const cfg = map[estado] || { variant: "secondary" as const, label: estado };
    return <Badge variant={cfg.variant}>{cfg.label}</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Suscripción</h1>
        <p className="text-muted-foreground">Gestiona tu plan de FolioListo</p>
      </div>

      {/* Estado actual */}
      {suscripcion && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-3">
              <CreditCard className="h-5 w-5" />
              Suscripción actual
              {estadoBadge(suscripcion.estado)}
            </CardTitle>
            <CardDescription>
              Plan: <strong>{suscripcion.plan.nombre}</strong> — ${suscripcion.plan.precio.toLocaleString("es-CL")} CLP / {suscripcion.plan.periodio.toLowerCase()}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {suscripcion.fechaFin && (
              <p className="text-sm">
                Vence el: <strong>{new Date(suscripcion.fechaFin).toLocaleDateString("es-CL")}</strong>
              </p>
            )}
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={handleSync}>
                Sincronizar estado
              </Button>
              {suscripcion.estado === "ACTIVA" && (
                <Button variant="destructive" size="sm" onClick={handleCancelar}>
                  Cancelar suscripción
                </Button>
              )}
              {suscripcion.estado === "PENDIENTE_PAGO" && suscripcion.mpPreapprovalId && (
                <Button size="sm" onClick={handleSync}>
                  Ya pagué — verificar
                </Button>
              )}
            </div>

            {suscripcion.pagos.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold mb-2">Pagos recientes</h4>
                <div className="space-y-1">
                  {suscripcion.pagos.map((p) => (
                    <div key={p.id} className="flex items-center justify-between text-sm border-b pb-1">
                      <span>${p.monto.toLocaleString("es-CL")} CLP</span>
                      <Badge variant={p.estado === "APROBADO" ? "default" : "secondary"}>
                        {p.estado}
                      </Badge>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Cupón */}
      {suscripcion && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-3">
              <Ticket className="h-5 w-5" />
              Tengo un cupón
            </CardTitle>
            <CardDescription>Valida y aplica tu código de descuento</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="space-y-1.5">
              <div className="flex gap-2">
                <Input
                  placeholder="INGRESA TU CÓDIGO"
                  value={cuponCodigo}
                  onChange={(e) => {
                    setCuponCodigo(e.target.value.toUpperCase());
                    setCuponValido(null);
                  }}
                  className={`flex-1 font-mono uppercase transition-colors ${
                    cuponValido && !cuponValido.valido
                      ? "border-destructive focus-visible:ring-destructive"
                      : cuponValido?.valido
                      ? "border-emerald-600 focus-visible:ring-emerald-600 bg-emerald-500/5"
                      : ""
                  }`}
                />
                <Button variant="outline" onClick={handleValidarCupon} disabled={validandoCupon || !cuponCodigo.trim()}>
                  {validandoCupon ? <Loader2 className="h-4 w-4 animate-spin" /> : "Validar"}
                </Button>
              </div>
              {cuponValido && !cuponValido.valido && (
                <p className="text-xs text-destructive font-medium flex items-center gap-1 mt-1">
                  <span>✗</span>
                  <span>{cuponValido.error || "Cupón no válido"}</span>
                </p>
              )}
            </div>
            {cuponValido?.valido && (
              <div className="flex items-center justify-between p-2.5 rounded-lg bg-emerald-500/10 border border-emerald-500/30">
                <span className="text-sm font-medium text-emerald-600 dark:text-emerald-400">
                  ✓ {cuponValido.tipo} ({cuponValido.valor}{cuponValido.tipo === "PORCENTAJE" ? "%" : cuponValido.tipo === "MONTO_FIJO" ? " CLP" : " mes(es)"})
                </span>
                <Button size="sm" onClick={handleAplicarCupon} disabled={aplicandoCupon} className="bg-emerald-600 hover:bg-emerald-700 text-white">
                  {aplicandoCupon ? <Loader2 className="h-4 w-4 animate-spin" /> : "Aplicar"}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Planes disponibles */}
      {!suscripcion?.estado || suscripcion.estado === "CANCELADA" || suscripcion.estado === "EXPIRADA" ? (
        <div className="grid gap-4 md:grid-cols-2">
          {planes.map((plan) => (
            <Card key={plan.id}>
              <CardHeader>
                <CardTitle>{plan.nombre}</CardTitle>
                <CardDescription>{plan.descripcion}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="text-3xl font-bold">
                  ${plan.precio.toLocaleString("es-CL")}
                  <span className="text-sm font-normal text-muted-foreground"> CLP / {plan.periodo.toLowerCase()}</span>
                </div>
                {plan.periodo === "ANUAL" && (
                  <p className="text-sm text-green-600">Ahorra 20% vs plan mensual</p>
                )}
                <Button
                  className="w-full"
                  onClick={() => handleCrear(plan.id)}
                  disabled={creating === plan.id}
                >
                  {creating === plan.id ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Creando...
                    </>
                  ) : (
                    "Suscribir"
                  )}
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : null}

      {/* Link de pago MP si está pendiente */}
      {initPoint && (
        <Card className="border-blue-500">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <ExternalLink className="h-5 w-5 text-blue-500" />
              <a href={initPoint} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">
                Ir a Mercado Pago para completar el pago
              </a>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
